/**
 * Preview Transactions Helper
 *
 * Fetches and filters the external CSV transaction file for a single shareholder,
 * returning the rows as a per-share-class map. Used by the `/admin/profile`
 * shareholder preview path so templates can render the correct per-class basis
 * for EXTERNAL_DATA clients (e.g. PharmaJet) — where basis is derived from
 * transactions, not Contribution events.
 *
 * Design constraints (to avoid the #5076 revert's side effects):
 * - NEVER mutates clientData, userData, sessionStorage, or any global state.
 * - NEVER dispatches events.
 * - NEVER touches the dashboard's own transaction load path.
 * - Admin-preview-only — only callers on /admin/profile should use this.
 *
 * The filter + row-to-transaction mapping mirrors `loadTheseTransactions` at
 * `src/shared_assets/index.js:3729-3783`. Some duplication is intentional —
 * extracting a shared helper would require touching `getStandardTransactionsFromFile`,
 * which is part of the dashboard data-loading flow. Keeping this separate
 * protects the dashboard from inadvertent breakage.
 *
 * If `loadTheseTransactions` changes (new field, new client-specific branch),
 * mirror it here.
 */

import { cleanCSV } from './string-utils.js';

/**
 * In-memory cache keyed by CSV filename. Scoped to the browser tab's session.
 * Cleared by page reload, which is acceptable for admin tooling.
 */
const csvRowCache = new Map();

/**
 * Fetch the external-data CSV (once per session per file) and filter it for a
 * single shareholder's transactions.
 *
 * @param {Object} options
 * @param {Object} options.clientData - Full client configuration (reads `.transactions_data`)
 * @param {string} options.userid - The previewed shareholder's userid (email)
 * @param {Object} [options.clientFlags] - { isPHJ, isACR } for column-specific handling. Defaults to window.* flags.
 * @param {boolean} [options.useTaxBasis] - Same meaning as `USE_TAX_BASIS` in index.js. Defaults to window.USE_TAX_BASIS.
 * @param {Function} [options.getContentUrlFn] - Injected for tests. Defaults to window.getContentUrl.
 * @param {Function} [options.fetchFn] - Injected for tests. Defaults to globalThis.fetch.
 * @returns {Promise<Object|null>} Map of { [shareClass]: Transaction[] } or null if client has no transactions_data.
 */
export async function fetchTransactionsForPreview({
	clientData,
	userid,
	clientFlags = null,
	useTaxBasis = null,
	getContentUrlFn = null,
	fetchFn = null,
}) {
	if (!clientData?.transactions_data || !userid) return null;

	const flags = clientFlags || {
		isPHJ: typeof window !== 'undefined' && !!window.isPHJ,
		isACR: typeof window !== 'undefined' && !!window.isACR,
	};
	const useTB = useTaxBasis !== null ? useTaxBasis : (typeof window !== 'undefined' && !!window.USE_TAX_BASIS);
	const getUrl = getContentUrlFn || (typeof window !== 'undefined' ? window.getContentUrl : null);
	const doFetch = fetchFn || (typeof globalThis !== 'undefined' ? globalThis.fetch : null);

	if (!getUrl || !doFetch) {
		console.warn('[preview-transactions] Missing getContentUrl or fetch — cannot load CSV');
		return null;
	}

	const csvKey = clientData.transactions_data;
	let rows = csvRowCache.get(csvKey);
	if (!rows) {
		const url = await getUrl(csvKey);
		const response = await doFetch(url);
		const text = await response.text();
		rows = cleanCSV(text);
		// Same header-column collapse as getStandardTransactionsFromFile (index.js:3705-3706)
		const certCount = rows.reduce((total, a) => total + (a[4]?.match(/#/g) || []).length, 0);
		if (certCount > 8) rows.forEach((a) => a.splice(3, 1));
		csvRowCache.set(csvKey, rows);
	}

	const me = userid.toLowerCase();
	const result = {};
	const seenNridsByClass = {};

	rows.forEach((r) => {
		const shareClass = r[0];
		if (!shareClass || shareClass.endsWith('-Debt')) return;

		const sender = r[6]?.trim().toLowerCase();
		const receiver = r[8]?.trim().toLowerCase();
		if (sender !== me && receiver !== me) return;

		const quantity = Number(r[9]?.replace(/[^.\d]/g, ''));
		if (!(quantity > 0)) return;

		const nrid = r[4];
		if (!seenNridsByClass[shareClass]) seenNridsByClass[shareClass] = new Set();
		if (seenNridsByClass[shareClass].has(nrid)) return;
		seenNridsByClass[shareClass].add(nrid);

		const newTransObj = {
			date: (Date.parse(r[1]) / 1000).toString(),
			notes: r[3],
			nrid: nrid,
			quantity: quantity.toString(),
			receiver: receiver,
			receiverName: r[7]?.trim(),
			securityType: r[2],
			sender: sender,
			senderName: r[5],
			shareClass: shareClass,
			sharePrice: flags.isACR ? '1' : r[11]?.replace(/[^.\d]/g, '') || r[10]?.replace(/[^.\d]/g, '') || '',
		};

		if (flags.isPHJ || useTB) {
			newTransObj.strikePrice = r[15]?.replace(/[^.\d]/g, '');
			const exp = Date.parse(r[16]);
			newTransObj.expDate = isNaN(exp) ? '' : (exp / 1000).toString();
		}
		if (r[17]) newTransObj.taxBasis = r[17].replace(/[^-.\d]/g, '');

		if (!result[shareClass]) result[shareClass] = [];
		result[shareClass].push(newTransObj);
	});

	return result;
}

/**
 * Clear the cached CSV rows. Call between tests or when the admin switches clients.
 */
export function clearPreviewTransactionsCache() {
	csvRowCache.clear();
}
